import AppsIcon from '@mui/icons-material/Apps'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import { Button, Chip, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import { Box } from '@mui/system'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { subDays, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { StyledTooltip } from 'src/components/Tooltip/StyledTooltip'
import { AlertContext } from 'src/context/AlertContext'
import { DashboardViewContext } from 'src/context/DashboardViewContext'

interface Props {
  onPeriodChange?: (
    periodValue: any,
    periodName: any,
    initialDate: any,
    finalDate: any
  ) => void
  onEdit?: (editState: boolean) => void
  onChangeDateInitial?: (initialDate: any) => void
  onChangeDateFinal?: (initialDate: any) => void
}

export const DashboardHeader = ({ onPeriodChange, onChangeDateInitial, onChangeDateFinal }: Props) => {
  const { t } = useTranslation()
  const periodOptions = [
    { value: '1d', name: t('date.label.today'), days: 1 },
    { value: '7d', name: `7 ${t('date.label.days')}`, days: 7 },
    { value: '30d', name: `30 ${t('date.label.days')}`, days: 30 },
    { value: '90d', name: t('date.label.quarter'), days: 90 },
    { value: '365d', name: t('date.label.year'), days: 365 },
    { value: 'custom', name: t('date.label.all'), days: 5000 },
  ]
  const [period, setPeriod] = useState(periodOptions[0])
  const [initialDate, setInitialDate] = useState<Date | null>(new Date())
  const [finalDate, setFinalDate] = useState<Date | null>(new Date())
  const [open, setOpen] = useState(false)
  const { isEditMode, handleToggleEdit, writeResult } = useContext(DashboardViewContext)
  const { createAlert } = useContext(AlertContext)

  const handlePeriodChange = (option: typeof periodOptions[number]) => {
    const now = new Date()
    const start = subDays(now, option.days)

    setInitialDate(start)
    setFinalDate(now)
    setPeriod(option)

    const initialDateFormatted = format(start, 'dd/MM/yyyy')
    const finalDateFormatted = format(now, 'dd/MM/yyyy')

    onPeriodChange?.(
      option.value,
      option.name,
      initialDateFormatted,
      finalDateFormatted
    )
  }

  return (
    <Box
      onClick={isEditMode ? handleToggleEdit : null}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        maxWidth: '950px',
        width: '100%',
        justifyContent: 'center',
        gap: '20px',
        pr: 1,
        mt: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#FF5E1E',
        }}
      >
        <AppsIcon />
        <Typography
          variant="h6"
          sx={{
            fontWeight: '500',
            fontSize: '20px',
            lineHeight: '160%',
            letterSpacing: ' 0.15px',
            color: ' #FF5E1E',
            ml: '8px',
            mb: '-3px',
          }}
        >
          {t('painel.title')}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: '400',
                fontSize: '34px',
                lineHeight: '160%',
                letterSpacing: ' 0.15px',
                width: '130px',
              }}
            >
              {period?.name}
            </Typography>
          </Box>
          <Box sx={{ ml: '80px' }}>
            { periodOptions.map((option) => (
              <Chip
                key={option.value}
                sx={{
                  cursor: 'pointer',
                  ':hover': {
                    border: '1px solid #00000042',
                    color: '#00000099',
                    bgcolor: 'rgba(255, 94, 30, 0.08)',
                  },
                  borderColor: period.value === option.value ? '#FF5E1E' : '',
                  color: period.value === option.value ? '#FF5E1E' : '',
                  bgcolor: period.value === option.value ? 'rgba(255, 94, 30, 0.08)' : '',
                  mr: '10px',
                }}
                onClick={() => handlePeriodChange(option)}
                variant="outlined"
                label={option.name}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Box sx={{ height: '54px', display: 'flex', alignItems: 'flex-end' }}>
            {isEditMode && (
              <Button
                variant="contained"
                sx={{ mr: 1 }}
                onClick={() => {
                  if (writeResult() === false) {
                    createAlert('/', t('dasboard.card.notpossible.hide.cards'), 'error')
                    return
                  }
                  handleToggleEdit()
                }}
              >
                OK
              </Button>
            )}
          </Box>

          <Box
            sx={{
              '.btns-drop': {
                border: 'none',
                fontSize: '14px',
                fontWeight: '400',
                fontFamily: 'roboto',
                background: 'white',
                padding: '8px 16px',
                margin: '0 0 8px 0',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: 'rgba(255, 94, 30, 0.04)',
                  color: '#FF5E1E',
                },
              },
            }}
          >
            <StyledTooltip content={t('dashboard.button.help')}>
              <Button
                onClick={() => {
                  handleToggleEdit()
                  setOpen(false)
                }}
                variant="outlined"
                sx={{
                  borderRadius: '4px 0px 0px 4px',
                  minWidth: '48px',
                  width: '48px',
                  height: '48px',
                  color: isEditMode ? '#FF5E1E' : 'rgba(0, 0, 0, 0.6)',
                  borderColor: isEditMode ? '#FF5E1E' : 'rgba(0, 0, 0, 0.12)',
                  backgroundColor: isEditMode ? 'rgba(255, 94, 30, 0.08);' : '',
                  ':hover': {
                    color: '#FF5E1E',
                  },
                }}
              >
                <RemoveRedEyeOutlinedIcon />
              </Button>
            </StyledTooltip>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', mt: 2.5, gap: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DatePicker
            label={t('date.initial')}
            value={initialDate}
            inputFormat="dd/MM/yyyy"
            onChange={(value) => {
              if (!value) return
              setInitialDate(value)
              const formatted = format(value, 'MM/dd/yyyy')
              onChangeDateInitial(formatted)
            }}
            renderInput={(params) => (
              <TextField {...params} sx={{ width: 305 }} />
            )}
          />
          <DatePicker
            label={t('date.final')}
            value={finalDate}
            inputFormat="dd/MM/yyyy"
            onChange={(value) => {
              if (!value) return
              setFinalDate(value)
              const formatted = format(value, 'MM/dd/yyyy')
              onChangeDateFinal(formatted)
            }}
            shouldDisableDate={(date) =>
              new Date(date) < new Date(initialDate)
            }
            renderInput={(params) => (
              <TextField {...params} sx={{ width: 305 }} />
            )}
          />
        </LocalizationProvider>
      </Box>
    </Box>
  )
}
